import { createContext, memo, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TabHeaderProps } from "./TabsHeader";
import { TabStrategyType } from "./types";

type TabMap = {
  [key: string]: TabHeaderDescription
}

export type TabHeaderDescription = TabHeaderProps & {
  id?: string
  order?: number
};

export type TabsContextProps = {
  activeTabId: string,
  setActiveTabId: (tabId: string) => void,
  strategy: TabStrategyType,
  registerTab: (id: string, props: TabHeaderDescription) => void,
  unregisterTab: (id: string) => void,
}

const TabsContext = createContext<TabsContextProps>({
  activeTabId: "",
  setActiveTabId: value => {
    throw new Error("Not implemented. Please use TabsContextProvider");
  },
  strategy: "hide",
  registerTab: (id, props) => {
    throw new Error("Not implemented. Please use TabsContextProvider");
  },
  unregisterTab: id => {
    throw new Error("Not implemented. Please use TabsContextProvider");
  },
});

export const TabsDescriptionContext = createContext<TabHeaderDescription[]>([]);

export const useTabsContext = () => useContext(TabsContext);
export const useTabsDescriptionContext = () => useContext(TabsDescriptionContext);

type TabContextProviderProps = {
  children?: ReactNode,
  defaultActiveTabId?: string,
  onActiveTabIdChanged?: (tabId: string) => void,
  strategy: TabStrategyType
};
export const TabsContextProvider = memo<TabContextProviderProps>((
  {
    children,
    defaultActiveTabId,
    strategy,
    onActiveTabIdChanged,
  }) => {
  const [ tabStore, setTabsStore ] = useState<TabMap>({});
  const [ activeTabId, setActiveTabIdInternal ] = useState("");

  const setActiveTabId = useCallback((tabId: string) => {
    setActiveTabIdInternal(tabId);
    onActiveTabIdChanged?.(tabId);
  }, [ onActiveTabIdChanged ]);

  const tabIds = Object.keys(tabStore);
  const noActiveTab = !tabIds.find(x => x === activeTabId);
  const tabId = tabIds[0];

  useEffect(() => {
    if (noActiveTab) {
      defaultActiveTabId ? setActiveTabId(defaultActiveTabId) : setActiveTabId(tabId || "");
    }
  }, [ noActiveTab, tabId, defaultActiveTabId, setActiveTabId ]);

  const registerTab = useCallback((id: string, props: TabHeaderDescription) => {
    setTabsStore(prevState => ({
      ...prevState,
      [id]: props,
    }));
  }, [ setTabsStore ]);

  const unregisterTab = useCallback((id: string) => {
    setTabsStore(prevState => {
      const newState = { ...prevState };
      delete newState[id];
      return newState;
    });
  }, [ setTabsStore ]);

  const value = useMemo(() => ({
    activeTabId, setActiveTabId, unregisterTab, registerTab, strategy,
  }), [ setActiveTabId, activeTabId, unregisterTab, registerTab, strategy ]);

  const tabsDescription = useMemo(() => Object.values(tabStore), [ tabStore ]);

  return (
    <TabsContext.Provider value={value}>
      <TabsDescriptionContext.Provider value={tabsDescription}>
        {children}
      </TabsDescriptionContext.Provider>
    </TabsContext.Provider>
  );
});
